import type { FC, ReactElement } from 'react';
import { useCallback, useEffect } from 'react';

import type { BalanceType } from '../../../api/fetchers/userInfo/types';
import { useCompositeBalance } from '../../../store/compositeSelectors/useCompositeBalance';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useBetSlice } from '../../../store/slices/bets/selectors';
import { useChipSlice } from '../../../store/slices/chips/selectors';
import { chipActions } from '../../../store/slices/chips/slice';
import { ChipSelectionOrigin, type ChipValueType } from '../../../store/slices/chips/types';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { selectMaxAffordableChip } from '../../../utils/chips';

import { BarChipStack } from './barChipStack';
import { ClipChipStack } from './clipChipStack';
import { RevolverChipStack } from './revolverChipStack';
import type { ChipConfig, ChipStackProps, VerticalClipChipStackProps } from './types';
import { useSiblingChips } from './useSiblingChip';

export const useChipStackController = (): ChipStackProps => {
  const dispatch = useCoreDispatch();
  const areBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const userInfo = useGameStateSlice.userInfo();
  const totalBet = useBetSlice.totalBet();
  const balance = useCompositeBalance() as BalanceType;
  const totalBalance = balance.amount + balance.bonus;
  const isLowBalance = useBetSlice.isLowBalance(totalBalance);
  const chips = useChipSlice.getChips();
  const selectedChip = useChipSlice.getSelectedChip();
  const [previousChip, nextChip] = useSiblingChips(chips, selectedChip);

  const chipsConfigured: ChipConfig[] = chips.map((chip) => {
    if (totalBalance < chip.value) {
      return { ...chip, disabled: true }; // If any other reason will be found - make this to accept translation keys
    }
    return chip;
  });

  const onChipSelect = useCallback(
    (chip: ChipValueType) => {
      if (selectedChip && chip.weight === selectedChip.weight) return;

      dispatch(chipActions.setAndSyncSelectedChip({ ...chip, selectedBy: ChipSelectionOrigin.User })); // Save selected chip in playerConfig on server
    },
    [dispatch, selectedChip]
  );

  // Automatically select chip value on total bet change.
  // Does not allow chip value to increase, unless changed by user.
  useEffect(() => {
    const affordableChip = selectMaxAffordableChip(chips, selectedChip, userInfo, totalBet);

    if (affordableChip.weight !== selectedChip.weight) {
      dispatch(chipActions.setSelectedChip({ ...affordableChip, selectedBy: ChipSelectionOrigin.System }));
    }
  }, [chips, dispatch, selectedChip, totalBet]); // fixme: `userInfo` as dependency resets to first chip after placing bets, investigate why

  return {
    areBetsOpen,
    chips: chipsConfigured,
    isLowBalance,
    nextChip,
    onChipSelect,
    previousChip,
    selectedChip,
  };
};

const Bar: FC = (props): ReactElement => {
  const chipStackProps = useChipStackController();
  return <BarChipStack {...props} {...chipStackProps} />;
};

const Clip: FC<VerticalClipChipStackProps> = (props: VerticalClipChipStackProps): ReactElement => {
  const chipStackProps = useChipStackController();
  return <ClipChipStack {...props} {...chipStackProps} disabled={chipStackProps.isLowBalance} />;
};

const Revolver: FC = (props): ReactElement => {
  const chipStackProps = useChipStackController();
  return <RevolverChipStack {...props} {...chipStackProps} disabled={chipStackProps.isLowBalance} />;
};

export const ChipStack = {
  Bar,
  Clip,
  Revolver,
};
